"use client";

import React, { useEffect, useId, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import Link from "next/link";
import { Icon, iconPaths } from "@/components/site/Icons";
import {
  contactNav,
  headerLinks,
  pathIsActive,
  serviceExtras,
  serviceMenu,
  servicesMenuIsActive,
  type NavItem,
} from "@/content/navigation";

function Wordmark() {
  return (
    <Link href="/" aria-label="Andrew Leger, home" className="flex min-h-11 min-w-0 shrink items-center gap-2 no-underline min-[420px]:gap-3">
      <img
        src="/images/aleger-color-logo.svg"
        alt=""
        width={60}
        height={30}
        className="block h-[30px] w-[60px] shrink-0"
        loading="eager"
        fetchPriority="high"
        decoding="sync"
      />
      <span className="whitespace-nowrap text-base font-bold uppercase leading-7 text-slate-200 [font-family:Arial,Helvetica,sans-serif] sm:text-xl">
        Andrew Leger
      </span>
    </Link>
  );
}

function DropdownItem({ item, pathname, onNavigate }: { item: NavItem; pathname: string; onNavigate: () => void }) {
  const active = pathIsActive(pathname, item.href);
  return (
    <Link href={item.href} className="dd-item" aria-current={active ? "page" : undefined} onClick={onNavigate}>
      <span className="text-sm font-semibold text-white">{item.label}</span>
      {item.description ? <span className="text-[13px] leading-[18px] text-slate-400">{item.description}</span> : null}
    </Link>
  );
}

export default function HeaderClient() {
  const pathname = usePathname() ?? "/";
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const servicesButtonRef = useRef<HTMLButtonElement>(null);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const servicesMenuId = useId();
  const mobileMenuId = useId();
  const servicesActive = servicesMenuIsActive(pathname);

  useEffect(() => {
    setMobileMenuOpen(false);
    setServicesOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!servicesOpen && !mobileMenuOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      if (servicesOpen) {
        setServicesOpen(false);
        servicesButtonRef.current?.focus();
      }
      if (mobileMenuOpen) {
        setMobileMenuOpen(false);
        menuButtonRef.current?.focus();
      }
    };

    const onPointerDown = (event: PointerEvent) => {
      if (!headerRef.current?.contains(event.target as Node)) {
        setServicesOpen(false);
      }
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("pointerdown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("pointerdown", onPointerDown);
    };
  }, [servicesOpen, mobileMenuOpen]);

  const close = () => {
    setServicesOpen(false);
    setMobileMenuOpen(false);
  };

  return (
    <header
      ref={headerRef}
      className="relative border-b border-slate-800 bg-[rgba(15,23,42,0.82)] backdrop-blur-[14px]"
    >
      <div className="wrap flex h-[88px] items-center justify-between gap-2 min-[420px]:gap-6">
        <Wordmark />

        <nav className="nav-links flex items-center gap-7" aria-label="Primary">
          <div className="relative">
            <button
              ref={servicesButtonRef}
              type="button"
              className={`nav-btn${servicesOpen ? " open" : ""}${servicesActive ? " cur" : ""}`}
              aria-haspopup="true"
              aria-expanded={servicesOpen}
              aria-controls={servicesMenuId}
              onClick={() => setServicesOpen((open) => !open)}
            >
              Services <Icon className="chev" size={14} strokeWidth={2.5} d={iconPaths.chevronDown} />
            </button>
            {servicesOpen ? (
              <div
                id={servicesMenuId}
                className="dd-in absolute -left-4 top-[52px] z-[60] w-[340px] rounded-xl border border-slate-700 bg-slate-950 p-2 shadow-[0_20px_25px_-5px_rgba(0,0,0,0.1),0_30px_60px_-20px_#020617]"
              >
                {serviceMenu.map((item) => (
                  <DropdownItem key={item.href} item={item} pathname={pathname} onNavigate={close} />
                ))}
                <div aria-hidden="true" className="mx-2 my-1.5 h-px bg-slate-800" />
                {serviceExtras.map((item) => (
                  <DropdownItem key={item.href} item={item} pathname={pathname} onNavigate={close} />
                ))}
              </div>
            ) : null}
          </div>
          {headerLinks.map((item) => {
            const active = pathIsActive(pathname, item.href);
            return (
              <Link
                key={item.href}
                href={item.href}
                aria-current={active ? "page" : undefined}
                className={`nav-link${active ? " cur" : ""}`}
              >
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="flex shrink-0 items-center gap-1 min-[420px]:gap-2">
          <Link
            href={contactNav.href}
            aria-current={pathIsActive(pathname, contactNav.href) ? "page" : undefined}
            className="btn btn-primary"
          >
            {contactNav.label}
          </Link>
          <button
            ref={menuButtonRef}
            type="button"
            className="nav-btn burger w-11 justify-center px-0 text-slate-200"
            aria-label={mobileMenuOpen ? "Close main menu" : "Open main menu"}
            aria-expanded={mobileMenuOpen}
            aria-controls={mobileMenuId}
            onClick={() => setMobileMenuOpen((open) => !open)}
          >
            {mobileMenuOpen ? (
              <Icon size={22} d={["M18 6 6 18", "M6 6l12 12"]} />
            ) : (
              <Icon size={22} d={["M4 7h16", "M4 12h16", "M4 17h16"]} />
            )}
          </button>
        </div>
      </div>

      {mobileMenuOpen ? (
        <nav
          id={mobileMenuId}
          className="mobile-menu dd-in flex flex-col border-t border-slate-800 bg-slate-950 px-4 pb-5 pt-3"
          aria-label="Mobile"
        >
          <span className="px-3.5 py-2 text-xs font-semibold uppercase tracking-[0.08em] text-slate-400">Services</span>
          {[...serviceMenu, ...serviceExtras].map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className="dd-item"
              aria-current={pathIsActive(pathname, item.href) ? "page" : undefined}
              onClick={close}
            >
              <span className="text-[15px] font-semibold text-white">{item.label}</span>
            </Link>
          ))}
          <div aria-hidden="true" className="my-2 h-px bg-slate-800" />
          {headerLinks.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className="dd-item"
              aria-current={pathIsActive(pathname, item.href) ? "page" : undefined}
              onClick={close}
            >
              <span className="text-[15px] font-semibold text-white">{item.label}</span>
            </Link>
          ))}
        </nav>
      ) : null}
    </header>
  );
}
